import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db } from './../../../firebase.js';
import { getAdminEvalGroupLabel } from './../../../common/adminEvalLabel.js';
import './WeeklyUsageUnitsPage.css';

function WeeklyUsageUnitsPage() {
    const { t } = useTranslation();
    const [unitSections, setUnitSections] = useState([]);

    const baseColors = [
        '#00C375',
        '#74D5F2',
        '#BD92F4',
        '#73FFAB',
        '#FF8989',
        '#81ECF3',
        '#53C0FF',
        '#ED9BD2',
        '#FF89B0',
        '#90AAFF',
        '#81F3AD'
    ];

    const getLastSevenIsoDays = () => {
        const now = new Date();
        const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        const days = [];
        for (let offset = 6; offset >= 0; offset -= 1) {
            const day = new Date(todayUtc);
            day.setUTCDate(todayUtc.getUTCDate() - offset);
            days.push(day.toISOString().split('T')[0]);
        }
        return days;
    };

    const getIsoDayFromTimestamp = (timestampMs) => {
        const date = new Date(Number(timestampMs));
        if (Number.isNaN(date.getTime())) return null;
        const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
        return utcDate.toISOString().split('T')[0];
    };

    const getWeekdayLabelFromIsoDay = (isoDay) => {
        const date = new Date(`${isoDay}T00:00:00Z`);
        if (Number.isNaN(date.getTime())) return isoDay;
        const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return weekdays[(date.getUTCDay() + 6) % 7];
    };

    const hexToRgb = (hex) => {
        const bigint = Number.parseInt(hex.slice(1), 16);
        return {
            r: (bigint >> 16) & 255,
            g: (bigint >> 8) & 255,
            b: bigint & 255
        };
    };

    const getCellBackgroundColor = (baseColor, count, maxCount) => {
        if (count <= 0 || maxCount <= 0) return 'rgba(255, 255, 255, 0.1)';
        const colorObj = hexToRgb(baseColor);
        const opacity = Math.max(count / maxCount, 0.15);
        return `rgba(${colorObj.r},${colorObj.g},${colorObj.b},${opacity})`;
    };

    const extractEvalLabel = (evalValue) => getAdminEvalGroupLabel(evalValue) || 'UNDEFINED';

    const weekIsoDays = useMemo(() => getLastSevenIsoDays(), []);

    useEffect(() => {
        const loadWeeklyUsageByUnit = async () => {
            try {
                const startMs = new Date(`${weekIsoDays[0]}T00:00:00Z`).getTime();
                const [unitsSnapshot, excludedSnapshot] = await Promise.all([
                    getDocs(collection(db, 'units')),
                    getDoc(doc(db, 'unit_filter_environments', 'excluded_from_statistics'))
                ]);

                const excludedData = excludedSnapshot.exists() ? (excludedSnapshot.data() || {}) : {};
                const excludedUnitIds = new Set(
                    (Array.isArray(excludedData.units) ? excludedData.units : []).map((unitId) => String(unitId))
                );

                const unitPromises = [];

                unitsSnapshot.forEach((unitDoc) => {
                    const unit = unitDoc.data() || {};
                    const unitId = String(unit.unit_id || unitDoc.id || '');
                    if (!unitId || excludedUnitIds.has(unitId)) return;

                    const recordsQuery = query(
                        collection(db, 'units', unitId, 'inference-records'),
                        where('timestamp', '>=', startMs)
                    );

                    unitPromises.push(
                        getDocs(recordsQuery).then((recordsSnapshot) => {
                            const usageByEval = {};
                            recordsSnapshot.forEach((recordDoc) => {
                                const data = recordDoc.data() || {};
                                if (data.is_test === true) return;
                                const timestamp = Number(data.timestamp);
                                if (!Number.isFinite(timestamp)) return;
                                const isoDay = getIsoDayFromTimestamp(timestamp);
                                if (!isoDay) return;

                                const evalLabel = extractEvalLabel(data.eval);
                                usageByEval[evalLabel] = usageByEval[evalLabel] || {};
                                usageByEval[evalLabel][isoDay] = (usageByEval[evalLabel][isoDay] || 0) + 1;
                            });

                            const rows = Object.keys(usageByEval)
                                .sort((a, b) => a.localeCompare(b))
                                .map((evalLabel) => ({
                                    evalLabel,
                                    usage: weekIsoDays.map((day) => Number(usageByEval[evalLabel][day] || 0))
                                }));

                            return { unitId, rows };
                        })
                    );
                });

                const sections = (await Promise.all(unitPromises))
                    .filter((section) => section.rows.length > 0)
                    .sort((a, b) => a.unitId.localeCompare(b.unitId))
                    .map((section, index) => ({
                        ...section,
                        color: baseColors[(index + 1) % baseColors.length]
                    }));

                setUnitSections(sections);
            } catch (error) {
                console.log('Error calculating weekly usage by unit on page open:', error);
                setUnitSections([]);
            }
        };

        loadWeeklyUsageByUnit();
    }, [weekIsoDays]);

    const leftSections = useMemo(
        () => unitSections.filter((_, index) => index % 2 === 0),
        [unitSections]
    );
    const rightSections = useMemo(
        () => unitSections.filter((_, index) => index % 2 === 1),
        [unitSections]
    );

    return (
        <div className="weekly-usage-page">
            <div className="weekly-usage-columns">
                <div className="weekly-usage-column">
                    {leftSections.map((section) => (
                        <div key={section.unitId} className="weekly-category-card">
                            <div className="weekly-category-title" style={{ color: section.color }}>
                                {section.unitId}
                            </div>
                            <div className="weekly-day-labels">
                                {weekIsoDays.map((isoDay, index) => (
                                    <div key={index} className="weekly-day-label">
                                        {t(getWeekdayLabelFromIsoDay(isoDay))}
                                    </div>
                                ))}
                            </div>
                            <div className="weekly-rows">
                                {section.rows.map((row) => {
                                    const maxValue = Math.max(...row.usage, 0);
                                    return (
                                        <div key={row.evalLabel} className="weekly-row">
                                            <div className="weekly-row-label">{t(row.evalLabel)}</div>
                                            <div className="weekly-values">
                                                {row.usage.map((value, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="weekly-cell"
                                                        style={{ background: getCellBackgroundColor(section.color, value, maxValue) }}
                                                    >
                                                        {value}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="weekly-usage-column">
                    {rightSections.map((section) => (
                        <div key={section.unitId} className="weekly-category-card">
                            <div className="weekly-category-title" style={{ color: section.color }}>
                                {section.unitId}
                            </div>
                            <div className="weekly-day-labels">
                                {weekIsoDays.map((isoDay, index) => (
                                    <div key={index} className="weekly-day-label">
                                        {t(getWeekdayLabelFromIsoDay(isoDay))}
                                    </div>
                                ))}
                            </div>
                            <div className="weekly-rows">
                                {section.rows.map((row) => {
                                    const maxValue = Math.max(...row.usage, 0);
                                    return (
                                        <div key={row.evalLabel} className="weekly-row">
                                            <div className="weekly-row-label">{t(row.evalLabel)}</div>
                                            <div className="weekly-values">
                                                {row.usage.map((value, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="weekly-cell"
                                                        style={{ background: getCellBackgroundColor(section.color, value, maxValue) }}
                                                    >
                                                        {value}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default WeeklyUsageUnitsPage;
